import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  CalendarCheck, CheckCircle2, Clock, ChevronDown, ChevronUp, Send,
  AlertCircle, UploadCloud, ShieldAlert, Sparkles, ArrowRight, Info
} from 'lucide-react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { getRoleDayPrefix } from '../../../helper/role-helper';

const Attendance = () => {
  const [attendance, setAttendance] = useState([]);
  const [todayData, setTodayData] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [todayUpdates, setTodayUpdates] = useState([]);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [markingAttendance, setMarkingAttendance] = useState(false);
  const [updateForm, setUpdateForm] = useState({
    taskId: '',
    updateNotes: '',
    githubLink: '',
    liveLink: '',
    updateType: 'Progress',
  });
  const [files, setFiles] = useState([]);

  const token = localStorage.getItem('portalToken') || localStorage.getItem('admin');
  const headers = { Authorization: `Bearer ${token}` };

  let currentUser = todayData?.user;
  if (!currentUser) {
    try {
      currentUser = JSON.parse(localStorage.getItem('portalUser')) || {};
    } catch (e) {
      currentUser = {};
    }
  }
  const roleDayPrefix = getRoleDayPrefix(currentUser);

  const fetchAttendance = async () => {
    try {
      const res = await axios.get(`${window.API_URL}/api/portal/intern/attendance`, { headers });
      setAttendance(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchTodayDailyTasks = async () => {
    try {
      const res = await axios.get(`${window.API_URL}/api/portal/shared/daily-tasks/today`, { headers });
      setTodayData(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchTasks = async () => {
    try {
      const res = await axios.get(`${window.API_URL}/api/portal/shared/tasks`, { headers });
      setTasks(res.data.filter(t => t.status !== 'Completed'));
    } catch (err) {
      console.error(err);
    }
  };

  const fetchTodayUpdates = async () => {
    try {
      const res = await axios.get(`${window.API_URL}/api/portal/shared/updates/today`, { headers });
      setTodayUpdates(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchAttendance();
    fetchTodayDailyTasks();
    fetchTasks();
    fetchTodayUpdates();
  }, []);

  const today = new Date().toDateString();
  const todayAttendanceRecord = attendance.find(a => new Date(a.date).toDateString() === today);
  const isPresentToday = todayAttendanceRecord?.status === 'Present' || todayData?.attendance?.status === 'Present';

  const todayTasks = todayData?.tasks || [];
  const metrics = todayData?.metrics || {
    totalTasks: todayTasks.length,
    completedTasks: todayTasks.filter(t => t.status === 'Completed').length,
    completionRate: todayTasks.length > 0 ? Math.round((todayTasks.filter(t => t.status === 'Completed').length / todayTasks.length) * 100) : 0,
    dayStatus: 'No Assignment'
  };

  const handleSubmitUpdate = async (e) => {
    e.preventDefault();
    if (!updateForm.taskId) {
      return Swal.fire('Select a Task', 'Please select a task to update.', 'warning');
    }
    if (!updateForm.updateNotes.trim()) {
      return Swal.fire('Add Notes', 'Please describe your progress for today.', 'warning');
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('updateNotes', updateForm.updateNotes);
      formData.append('githubLink', updateForm.githubLink);
      formData.append('liveLink', updateForm.liveLink);
      formData.append('updateType', updateForm.updateType);
      Array.from(files).forEach(file => formData.append('files', file));

      await axios.post(
        `${window.API_URL}/api/portal/shared/tasks/${updateForm.taskId}/update`,
        formData,
        { headers }
      );

      Swal.fire({
        icon: 'success',
        title: updateForm.updateType === 'Final' ? 'Task Submitted!' : 'Update Posted!',
        text: updateForm.updateType === 'Final'
          ? 'Your work has been submitted to your mentor for review.'
          : 'Your daily progress has been logged.',
        toast: true, position: 'top-end', timer: 3000, showConfirmButton: false,
      });

      setUpdateForm({ taskId: '', updateNotes: '', githubLink: '', liveLink: '', updateType: 'Progress' });
      setFiles([]);
      setShowUpdateForm(false);
      fetchTodayUpdates();
      fetchTodayDailyTasks();
      fetchAttendance();
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to post update.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const markAttendance = async () => {
    setMarkingAttendance(true);
    try {
      await axios.post(`${window.API_URL}/api/portal/intern/attendance`, {}, { headers });
      Swal.fire({
        icon: 'success',
        title: 'Attendance Marked!',
        text: 'You have checked in for today.',
        confirmButtonColor: '#f06600',
      });
      fetchAttendance();
      fetchTodayDailyTasks();
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Cannot Mark Attendance',
        text: err.response?.data?.message || 'Failed to mark attendance.',
        confirmButtonColor: '#f06600',
      });
    } finally {
      setMarkingAttendance(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl mx-auto text-gray-900 pb-16">
      {/* Page Title */}
      <div>
        <h2 className="text-3xl font-extrabold tracking-tight">Attendance & Daily Check-In</h2>
        <p className="text-sm text-gray-500 mt-1">
          {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </p>
      </div>

      {/* How Attendance Works Explainer Card */}
      <div className="bg-blue-50/80 border border-blue-200 rounded-3xl p-5 flex items-start gap-3.5 text-blue-950">
        <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
        <div className="text-xs sm:text-sm leading-relaxed space-y-1">
          <p className="font-bold text-blue-900">How Automatic Attendance Works at Beta Communes:</p>
          <ul className="list-disc list-inside space-y-0.5 text-blue-800 text-xs">
            <li><strong>All tasks completed:</strong> Once all tasks for today are completed and approved, attendance turns <strong>Present</strong> automatically.</li>
            <li><strong>Incomplete tasks:</strong> Pending tasks keep today's status as <strong>Incomplete</strong>.</li>
            <li><strong>No tasks assigned:</strong> You will never be penalized or marked absent on days without tasks.</li>
            <li><strong>Audit Overrides:</strong> Admins can log manual attendance overrides with verified reasons when appropriate.</li>
          </ul>
        </div>
      </div>

      {/* ===== TODAY'S STATUS CARD ===== */}
      <div className="bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-[#f06600]/10 via-amber-50 to-orange-50/50 p-6 sm:p-7 border-b border-gray-100">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <CalendarCheck className="w-8 h-8 text-[#f06600]" />
              <div>
                <h3 className="text-xl font-bold text-gray-900">Today's Attendance Status</h3>
                <p className="text-xs text-gray-500">
                  {todayData?.internshipDay ? `${roleDayPrefix} Day ${todayData.internshipDay}` : 'Daily Session'}
                </p>
              </div>
            </div>

            {/* Attendance Status Badge */}
            {isPresentToday ? (
              <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-black bg-emerald-100 text-emerald-800 border border-emerald-300 shadow-sm">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Present
              </span>
            ) : todayAttendanceRecord?.source === 'admin_override' ? (
              <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-black bg-amber-100 text-amber-900 border border-amber-300 shadow-sm">
                <ShieldAlert className="w-4 h-4 text-amber-600" /> Override: {todayAttendanceRecord.status}
              </span>
            ) : todayTasks.length === 0 ? (
              <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold bg-gray-100 text-gray-700">
                No Assignment
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold bg-yellow-100 text-yellow-900 border border-yellow-300">
                <Clock className="w-4 h-4 text-yellow-600" /> Incomplete / Pending Tasks
              </span>
            )}
          </div>

          {/* Today's Tasks Progress Box */}
          {todayTasks.length > 0 ? (
            <div className="bg-white/80 backdrop-blur-md rounded-2xl p-4 border border-gray-200/80 space-y-2.5">
              <div className="flex justify-between items-center text-xs font-bold text-gray-700">
                <span>Today's Task Completion</span>
                <span className="text-[#f06600]">
                  {metrics.completedTasks} / {metrics.totalTasks} Tasks Completed ({metrics.completionRate}%)
                </span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ${
                    metrics.completionRate === 100
                      ? 'bg-gradient-to-r from-emerald-500 to-green-500'
                      : 'bg-gradient-to-r from-[#f06600] to-amber-500'
                  }`}
                  style={{ width: `${metrics.completionRate}%` }}
                />
              </div>
              <div className="flex justify-between items-center pt-1">
                <span className="text-[11px] text-gray-500">
                  {metrics.completedTasks === metrics.totalTasks
                    ? '✓ All tasks completed! Present status earned.'
                    : `${metrics.totalTasks - metrics.completedTasks} task(s) remaining for today.`}
                </span>
                <Link
                  to="/dashboard/intern/tasks"
                  className="text-xs font-bold text-[#f06600] hover:underline inline-flex items-center gap-1"
                >
                  Manage Today's Tasks <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          ) : (
            <div className="bg-white/80 rounded-2xl p-4 border border-gray-200 text-xs text-gray-500 flex items-center justify-between">
              <span>No tasks assigned for today. You are excused from daily task requirements.</span>
              <button
                onClick={markAttendance}
                disabled={markingAttendance || isPresentToday}
                className="px-3.5 py-1.5 bg-[#f06600] hover:bg-[#d05800] disabled:opacity-50 text-white rounded-xl font-bold text-xs shadow-sm transition-all"
              >
                {isPresentToday ? 'Checked In' : 'Manual Check-in'}
              </button>
            </div>
          )}

          {/* Quick Action Toggle for updates if still pending */}
          {!isPresentToday && tasks.length > 0 && (
            <div className="pt-4 flex gap-3">
              <button
                onClick={() => setShowUpdateForm(prev => !prev)}
                className="flex-1 py-2.5 px-4 bg-white hover:bg-gray-50 border border-gray-200 rounded-xl text-xs font-bold text-gray-700 transition-colors flex items-center justify-center gap-1.5"
              >
                <Send className="w-3.5 h-3.5 text-[#f06600]" />
                {showUpdateForm ? 'Hide Quick Progress Form' : 'Log Quick Task Progress'}
              </button>
              <Link
                to="/dashboard/intern/tasks"
                className="flex-1 py-2.5 px-4 bg-[#f06600] hover:bg-[#d05800] text-white rounded-xl text-xs font-bold transition-all shadow-sm flex items-center justify-center gap-1.5"
              >
                <Sparkles className="w-3.5 h-3.5" /> Go to Task Workspace
              </Link>
            </div>
          )}
        </div>

        {/* ===== QUICK INLINE TASK UPDATE FORM ===== */}
        {showUpdateForm && !isPresentToday && (
          <div className="p-6 border-b border-gray-100 bg-gray-50">
            <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2 text-sm">
              <Send className="w-4 h-4 text-[#f06600]" />
              Quick Daily Progress Note
            </h4>
            <form onSubmit={handleSubmitUpdate} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Select Active Task *</label>
                <select
                  required
                  value={updateForm.taskId}
                  onChange={e => setUpdateForm({ ...updateForm, taskId: e.target.value })}
                  className="w-full bg-white border border-gray-200 rounded-xl px-3 py-2 text-xs text-gray-800 focus:ring-2 focus:ring-[#f06600] outline-none font-medium"
                >
                  <option value="">Choose task...</option>
                  {tasks.map(t => (
                    <option key={t._id} value={t._id}>
                      {t.title} ({t.status})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Daily Work Notes *</label>
                <textarea
                  required
                  placeholder="Describe your progress, what you completed, any blockers..."
                  value={updateForm.updateNotes}
                  onChange={e => setUpdateForm({ ...updateForm, updateNotes: e.target.value })}
                  className="w-full bg-white border border-gray-200 rounded-xl px-3 py-2 text-xs text-gray-800 focus:ring-2 focus:ring-[#f06600] outline-none h-20 resize-none font-medium"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <input
                  type="url"
                  placeholder="GitHub / Figma URL (optional)"
                  value={updateForm.githubLink}
                  onChange={e => setUpdateForm({ ...updateForm, githubLink: e.target.value })}
                  className="bg-white border border-gray-200 rounded-xl px-3 py-2 text-xs text-gray-800 focus:ring-2 focus:ring-[#f06600] outline-none"
                />
                <input
                  type="url"
                  placeholder="Live Demo URL (optional)"
                  value={updateForm.liveLink}
                  onChange={e => setUpdateForm({ ...updateForm, liveLink: e.target.value })}
                  className="bg-white border border-gray-200 rounded-xl px-3 py-2 text-xs text-gray-800 focus:ring-2 focus:ring-[#f06600] outline-none"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-2.5 bg-[#f06600] hover:bg-[#d05800] text-white font-bold rounded-xl transition-all text-xs shadow-sm flex items-center justify-center gap-2"
              >
                <Send className="w-3.5 h-3.5" />
                {submitting ? 'Submitting...' : 'Post Progress'}
              </button>
            </form>
          </div>
        )}
      </div>

      {/* ===== ATTENDANCE HISTORY ===== */}
      <div className="bg-white rounded-3xl border border-gray-200 shadow-sm p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <CalendarCheck className="w-5 h-5 text-[#f06600]" />
          My Attendance History
        </h3>
        {attendance.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-8">No attendance records yet.</p>
        ) : (
          <div className="space-y-2.5">
            {attendance.map(a => {
              const date = new Date(a.date);
              const isToday = date.toDateString() === today;
              const isOverridden = a.source === 'admin_override';
              const isAuto = a.source === 'auto';

              return (
                <div
                  key={a._id}
                  className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-2xl border transition-all gap-2 ${
                    isToday ? 'bg-[#f06600]/5 border-[#f06600]/20' : 'bg-gray-50/70 border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-bold text-gray-900 text-sm">
                          {date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                        {isToday && (
                          <span className="bg-[#f06600] text-white text-[10px] font-extrabold px-2 py-0.2 rounded-full">
                            Today
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        {isOverridden ? (
                          <span className="text-[11px] font-semibold text-amber-700 inline-flex items-center gap-1">
                            <ShieldAlert className="w-3 h-3 text-amber-600" /> Admin Override
                            {a.overrideReason && ` — "${a.overrideReason}"`}
                          </span>
                        ) : isAuto ? (
                          <span className="text-[11px] font-semibold text-blue-700">
                            Auto (Task Completion)
                          </span>
                        ) : (
                          <span className="text-[11px] text-gray-400">
                            Manual Check-In
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <span className={`self-start sm:self-auto px-3 py-1 rounded-full text-xs font-bold ${
                    a.status === 'Present' ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' :
                    a.status === 'Late' ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' :
                    a.status === 'Excused' ? 'bg-blue-100 text-blue-800 border border-blue-200' :
                    'bg-rose-100 text-rose-800 border border-rose-200'
                  }`}>
                    {a.status || 'Present'}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Attendance;
